import { computed, inject, Injectable, signal, WritableSignal } from "@angular/core";
import { EarnTableStateEnum } from "../../models/enum/earn.enum";
import {
  EarnMask,
  EarnMaskWithMeta,
  Section,
} from "@bitwarden/web-vault/app/models/types/earn.types";
import { EarnConfigType } from "@bitwarden/web-vault/app/models/types/environement-config.type";
import { TokenService } from "@bitwarden/common/abstractions/token.service";
import { DataRepositoryService } from "@bitwarden/web-vault/app/services/DataRepository/data-repository.service";
import { EarnTableView } from "@bitwarden/web-vault/app/models/types/transaction.types";
import { PublishedData } from "@bitwarden/web-vault/app/gloss/earn/create-and-publish/create-and-publish.component";
import { INITIAL_DEFAULT_MASK } from "@bitwarden/web-vault/app/models/constants/earn.constants";
import { EarnLookup } from "@bitwarden/web-vault/app/services/earn/lookup.service";

@Injectable({
  providedIn: "root",
})
export class EarnService {
  tokenService = inject(TokenService);
  dataRepositoryService = inject(DataRepositoryService);
  earnLookup = inject(EarnLookup);

  breadCrumbState: WritableSignal<EarnTableStateEnum> = signal<EarnTableStateEnum>(
    EarnTableStateEnum.DRAFT,
  );

  defaultPageSize = signal<number>(20);
  /** The bundle mask on display. When selected, will mask transactions with respect to it*/
  selectedMask: WritableSignal<EarnMask | null> = signal<EarnMask | null>(null);

  maskList: WritableSignal<EarnMask[]> = signal<EarnMask[]>([]);

  selectedMaskVersion = computed<string>(() => {
    const mask = this.selectedMask();
    return this.getMaskDisplayVersion(mask);
  });

  maskListWithMeta: EarnMaskWithMeta[] = [];

  /** Active section in earn*/
  readonly activeSection = signal<Section>("history");

  async updateState() {
    const maskList: EarnMask[] = [];

    const masks = await this.getJsonMask();

    for (const mask of masks.items) {
      mask.data.publishedAt = new Date(mask.data.publishedAt);
      maskList.push(mask.data);
    }

    this.maskList.set(maskList);
    this.maskListWithMeta = masks.items;
    const bundle = await this.getJsonBundle();
  }

  lookupToMask() {
    const lookupMask = this.earnLookup.lookupMask();
    this.selectedMask.set({
      ...this.selectedMask(),
      maskId: this.selectedMask().maskId || crypto.randomUUID(),
      columns: Object.fromEntries(lookupMask.columns),
      rows: Object.fromEntries(lookupMask.rows),
      cells: Object.fromEntries(
        Array.from(lookupMask.cells.entries()).map(([transactionId, columnMap]) => [
          transactionId,
          Object.fromEntries(columnMap),
        ]),
      ),
      appliedToArray: Array.from(lookupMask.appliedToRows),
      publishedAt: this.selectedMask().publishedAt ? this.selectedMask().publishedAt : new Date(),
      updatedAt: new Date(),
    });
  }

  initializeMask(initialMask: EarnMask = INITIAL_DEFAULT_MASK) {
    this.selectedMask.set(initialMask);
    // reset lookup based on new mask
    this.earnLookup.maskToLookup(initialMask);
  }

  async getJsonMask() {
    const userId = await this.tokenService.getUserId();
    const earnConfig = process.env.EARN as unknown as EarnConfigType;
    const url = `${earnConfig.url}/${earnConfig.apiStage}`;
    const path = earnConfig.endpoint.mask.post.replace(":userId", userId);

    return await this.dataRepositoryService.send("GET", path, null, true, true, url);
  }

  async getJsonBundle() {
    const userId = await this.tokenService.getUserId();
    const earnConfig = process.env.EARN as unknown as EarnConfigType;
    const url = `${earnConfig.url}/${earnConfig.apiStage}`;
    const path = earnConfig.endpoint.bundle.post.replace(":userId", userId);

    return await this.dataRepositoryService.send("GET", path, null, true, true, url);
  }

  getMaskFile(mask: EarnMask): EarnMaskWithMeta {
    return this.maskListWithMeta.find((withMeta) => withMeta.data.maskId === mask.maskId);
  }

  getMaskDisplayVersion(mask: EarnMask): string {
    const date = new Date(mask.publishedAt);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");

    return `${year}${month}01.v${mask.version}`;
  }

  makeSelectedDraft() {
    this.selectedMask.set({
      ...this.selectedMask(),
      maskId: crypto.randomUUID(),
      updatedAt: new Date(),
    });
  }

  makeSelectedPublish() {
    this.selectedMask.set({
      ...this.selectedMask(),
      publishedAt: new Date(),
      status: "published",
    });
  }

  onAppliedToArrayChange(newAppliedToArray: string[]) {
    this.earnLookup.applySearchFilterToLookup(newAppliedToArray);
  }

  async saveSelectedMask() {
    const userId = await this.tokenService.getUserId();
    const earnConfig = process.env.EARN as unknown as EarnConfigType;
    const url = `${earnConfig.url}/${earnConfig.apiStage}`;
    const path = earnConfig.endpoint.mask.post.replace(":userId", userId);
    await this.dataRepositoryService.send(
      "POST",
      path,
      JSON.stringify(this.selectedMask()),
      true,
      true,
      url,
    );
  }

  async publishPreviewData(preview: EarnTableView[]) {
    const publishedData: (PublishedData | null)[] = preview.map((t) => {
      return {
        description: this.earnLookup.isCellMasked(t.id, "description") ? null : t.description,
        transactionDate: this.earnLookup.isCellMasked(t.id, "transactionDate")
          ? null
          : t.transactionDate,
        amount: this.earnLookup.isCellMasked(t.id, "amount") ? null : t.amount,
        account: this.earnLookup.isCellMasked(t.id, "account") ? null : t.account,
        inOut: this.earnLookup.isCellMasked(t.id, "inOut") ? null : t.inOut,
        currency: this.earnLookup.isCellMasked(t.id, "currency") ? null : t.currency,
        source: this.earnLookup.isCellMasked(t.id, "source") ? null : t.source,
        institution: this.earnLookup.isCellMasked(t.id, "institution") ? null : t.institution,
      };
    });

    const publishedBundle = {
      id: crypto.randomUUID(),
      data: publishedData,
      maskId: this.selectedMask().maskId,
    };

    if (this.isDraftMask()) {
      await this.publishDraft();
      // NB: don't re-read selectedMask().maskId here — the maskId captured above is already correct
      // (publishDraft spreads the existing mask), and re-reading picks up whatever another action
      // (Edit as New / tab switch to History, both reachable mid-save since #5459) wrote into
      // selectedMask while this await was in flight — corrupting or nulling the bundle's maskId (#5527).
      await this.saveBundleFile(publishedBundle);
      return;
    }

    this.selectedMask.set({
      ...this.selectedMask(),
      version: this.getMaskVersion(),
      publishedAt: new Date(),
      status: "published",
      updatedAt: new Date(),
    });

    await this.saveSelectedMask();

    // persist bundle — see the note above: the maskId captured before the await is authoritative, a
    // re-read here would inherit a concurrent action's write to selectedMask (#5527).
    await this.saveBundleFile(publishedBundle);
  }

  async saveBundleFile(json: any) {
    const userId = await this.tokenService.getUserId();
    const earnConfig = process.env.EARN as unknown as EarnConfigType;
    const url = `${earnConfig.url}/${earnConfig.apiStage}`;
    const path = earnConfig.endpoint.bundle.post.replace(":userId", userId);
    await this.dataRepositoryService.send("POST", path, JSON.stringify(json), true, true, url);
    await this.updateState();
  }

  private isDraftMask = () =>
    this.maskListWithMeta.find((m) => m.data.maskId === this.selectedMask().maskId)?.data.status ===
    "draft";

  private async publishDraft() {
    this.selectedMask.set({
      ...this.selectedMask(),
      publishedAt: new Date(),
      status: "published",
      updatedAt: new Date(),
      version: this.getMaskVersion(),
    });
    const maskFile = this.maskListWithMeta.find(
      (m) => m.data.maskId === this.selectedMask().maskId,
    );
    maskFile.data = this.selectedMask();

    const userId = await this.tokenService.getUserId();
    const earnConfig = process.env.EARN as unknown as EarnConfigType;
    const url = `${earnConfig.url}/${earnConfig.apiStage}`;
    const path = earnConfig.endpoint.mask.put
      .replace(":userId", userId)
      .replace(":fileId", maskFile.fileId);
    await this.dataRepositoryService.send(
      "PUT",
      path,
      JSON.stringify(this.selectedMask()),
      true,
      true,
      url,
    );

    await this.updateState();
  }

  getMaskVersion() {
    const lastMask: EarnMask = this.getLastMaskOfTheMonth();
    if (!lastMask) {
      return 1;
    }
    return lastMask.version + 1;
  }

  private getLastMaskOfTheMonth(): EarnMask {
    const masks = this.maskList();
    if (!masks) {
      return null;
    }
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    // Filter masks that belong to the current month
    const currentMonthMasks = masks.filter((m) => {
      const date = new Date(m.publishedAt);
      return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
    });

    // Return the one with the highest version number
    return currentMonthMasks.reduce((latest, current) => {
      if (!latest) {
        return current;
      }
      return current.version > latest.version ? current : latest;
    }, null);
  }

  editAsNew() {
    this.selectedMask.set({
      ...this.selectedMask(),
      version: this.selectedMask().version + 1,
      maskId: crypto.randomUUID(),
      status: "draft",
    });
    this.breadCrumbState.set(EarnTableStateEnum.DRAFT);
  }

  updateMaskForLatestTransactions() {
    if (!this.selectedMask()) {
      this.selectedMask.set(INITIAL_DEFAULT_MASK);
      return;
    }

    this.selectedMask.set({
      ...this.selectedMask(),
      status: "draft",
      maskId: crypto.randomUUID(),
    });
  }
}
